import React, {PureComponent, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import {VideoControls} from '../';
import {AGORA_VIDEO_PROFILE} from '../../../configs/constants';

export default class RtcLocalPlayer extends PureComponent {
  static propTypes = {
    isInterstitialPlaying: PropTypes.bool.isRequired,
    isVideoPlaying: PropTypes.bool.isRequired,
    stream: PropTypes.shape({}),
    resolution: PropTypes.string,
    children: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.array,
    ]).isRequired,
  };

  static defaultProps = {
    stream: null,
    resolution: AGORA_VIDEO_PROFILE,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const {stream} = nextProps;
    const {stream: prevStream} = prevState;

    return stream && !stream.isClosed() && stream === prevStream ? null : {isVideoMuted: false};
  }

  state = {
    videoId: uniqueId('video_'),
    accessDenied: false,
    isVideoMuted: false,
    isAudioMuted: false,
  };

  componentDidMount() {
    const {stream} = this.props;
    if (stream && !stream.isClosed()) {
      this.initStream(stream);
    }
  }

  componentDidUpdate(prevProps) {
    const {isInterstitialPlaying, isVideoPlaying, stream} = this.props;
    const {isAudioMuted} = this.state;
    const {
      stream: prevStream,
      isInterstitialPlaying: previsInterstitialPlaying,
      isVideoPlaying: prevIsVideoPlaying,
    } = prevProps;
    const newStreamOpened = !stream.isClosed();
    const prevStreamOpened = !prevStream.isClosed();
    const streamChanged = !prevStreamOpened || !newStreamOpened ||
    stream.getId() !== prevStream.getId();
    const isInterstitialPlayingbackChanged = isInterstitialPlaying !== previsInterstitialPlaying;
    const isVideoPlaybackChanged = isVideoPlaying !== prevIsVideoPlaying;
    if (prevStreamOpened && streamChanged) {
      this.closeStream(prevStream);
    }
    if (newStreamOpened && streamChanged) {
      this.initStream(stream);
    }
    if (newStreamOpened && (isInterstitialPlayingbackChanged || isVideoPlaybackChanged)) {
      const {disableAudio, enableAudio} = stream;
      if ((isInterstitialPlaying || isVideoPlaying) && !isAudioMuted) {
        disableAudio();
      }
      if (!isInterstitialPlaying && !isVideoPlaying && !isAudioMuted) {
        enableAudio();
      }
    }
  }

  componentWillUnmount() {
    const {stream} = this.props;
    if (stream && !stream.isClosed()) {
      this.closeStream(stream);
    }
  }

  onStreamInitFailed = (err) => {
    console.log('getUserMedia failed', err);
  };

  onAccessAllowed = () => {
    this.setState({accessDenied: false});
  };

  onAccessDenied = () => {
    this.setState({accessDenied: true});
  };

  onAudioClick = (isAudioMuted) => {
    const {stream: {disableAudio, enableAudio}, isInterstitialPlaying, isVideoPlaying} = this.props;
    const toggleAudio = isAudioMuted ? disableAudio : enableAudio;
    if (!isInterstitialPlaying && !isVideoPlaying) {
      this.setState({isAudioMuted}, toggleAudio);
    }
  };

  onVideoClick = (isVideoMuted) => {
    const {stream: {disableVideo, enableVideo}} = this.props;
    const toggleVideo = isVideoMuted ? disableVideo : enableVideo;
    toggleVideo();
    this.setState({isVideoMuted});
  };

  initStream = (stream) => {
    if (stream.isClosed()) {
      this.onStreamInitFailed();
    } else {
      const {resolution} = this.props;
      const {videoId} = this.state;
      stream.setVideoProfile(resolution);
      stream.play(videoId);
    }
  };

  closeStream = (stream) => {
    const streamId = stream.getId();
    stream.close();
    this.removePlayerDiv(streamId);
  };

  playerDiv = streamId => streamId && document.getElementById(`player_${streamId}`);

  // remove player div added directly by Agora.io
  removePlayerDiv = (streamId) => {
    const player = this.playerDiv(streamId);
    if (player) {
      player.parentNode.removeChild(player);
    }
  };

  render = () => {
    const {videoId, accessDenied, isVideoMuted} = this.state;
    const {children, isInterstitialPlaying, isVideoPlaying, stream} = this.props;
    const {hasVideo = () => false, hasAudio = () => false} = stream || {};
    const showAvatar = accessDenied || !hasVideo() || isVideoMuted;
    const videoClassnames = classNames(
      'team-player__video',
      {
        'team-player__video_muted': showAvatar,
      }
    );

    return (
      <Fragment>
        <div id={videoId} className={videoClassnames}>
          {showAvatar && children}
        </div>
        <VideoControls
          isVideoPlaying={isInterstitialPlaying || isVideoPlaying}
          hasAudio={hasAudio()}
          hasVideo={hasVideo()}
          onAudioClick={this.onAudioClick}
          onVideoClick={this.onVideoClick}
          stream={stream}
        />
      </Fragment>
    );
  };
}
